import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Schedule,
  Person,
  Email,
  Phone,
  Description,
  Business
} from '@mui/icons-material';
import { adminApi } from '../services/api.ts';

interface Verificacion {
  id: string;
  estado: string;
  documentos: {
    dni: string;
    selfie: string;
    personeria: string;
  };
  datos: any;
  fechaCreacion: string;
}

const VerificacionesPendientes: React.FC = () => {
  const [verificaciones, setVerificaciones] = useState<Verificacion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [selectedVerificacion, setSelectedVerificacion] = useState<Verificacion | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [action, setAction] = useState<'APPROVE' | 'REJECT'>('APPROVE');
  const [comments, setComments] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    cargarVerificaciones();
  }, []);

  const cargarVerificaciones = async () => {
    try {
      setLoading(true);
      const response = await adminApi.obtenerPendientesAprobacion();
      console.log('🔍 Respuesta del backend:', response.data);
      
      // Verificar si response.data es un array o si está dentro de data
      const verificacionesData = Array.isArray(response.data) ? response.data : 
                                (response.data?.data && Array.isArray(response.data.data)) ? response.data.data : [];
      
      setVerificaciones(verificacionesData);
    } catch (err: any) {
      console.error('❌ Error al cargar verificaciones:', err);
      setError('Error al cargar verificaciones: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalles = async (verificacion: Verificacion) => {
    try {
      console.log('🔍 Intentando obtener detalles para verificación ID:', verificacion.id);
      console.log('🔍 Verificacion original:', verificacion);
      
      const response = await adminApi.obtenerDocumentosVerificacion(verificacion.id);
      console.log('✅ Respuesta de detalles:', response);
      console.log('📋 Response.data:', response.data);
      console.log('👤 Cliente en response:', response.data.cliente);
      console.log('📁 Documentos en response:', response.data.documentos);
      
      const verificacionCompleta = { ...verificacion, ...response.data };
      console.log('🔧 Verificacion completa:', verificacionCompleta);
      console.log('👤 Cliente en verificacion completa:', verificacionCompleta.cliente);
      console.log('📁 Documentos en verificacion completa:', verificacionCompleta.documentos);
      
      setSelectedVerificacion(verificacionCompleta);
      console.log('✅ selectedVerificacion actualizado');
      setDialogOpen(true);
      console.log('✅ Dialog abierto');
    } catch (err: any) {
      console.error('❌ Error al cargar detalles:', err);
      console.error('❌ Error response:', err.response);
      setError('Error al cargar detalles: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleAprobar = (verificacion: Verificacion) => {
    setSelectedVerificacion(verificacion);
    setAction('APPROVE');
    setComments('');
    setActionDialogOpen(true);
  };

  const handleRechazar = (verificacion: Verificacion) => {
    setSelectedVerificacion(verificacion);
    setAction('REJECT');
    setComments('');
    setActionDialogOpen(true);
  };

  const confirmarAccion = async () => {
    if (!selectedVerificacion) return;

    try {
      setProcessing(true);
      await adminApi.aprobarVerificacion({
        verificationId: selectedVerificacion.id,
        action: action,
        comments: comments
      });

      setSuccess(`Verificación ${action === 'APPROVE' ? 'aprobada' : 'rechazada'} exitosamente`);
      setActionDialogOpen(false);
      setDialogOpen(false);
      setSelectedVerificacion(null);
      
      // Recargar la lista
      await cargarVerificaciones();
    } catch (err: any) {
      setError('Error al procesar la acción: ' + (err.response?.data?.message || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'PENDIENTE':
        return 'warning';
      case 'VERIFICADO':
        return 'success';
      case 'RECHAZADO':
        return 'error';
      default:
        return 'default';
    }
  };

  const getEstadoText = (estado: string) => {
    switch (estado) {
      case 'PENDIENTE':
        return 'Pendiente';
      case 'VERIFICADO':
        return 'Verificado';
      case 'RECHAZADO':
        return 'Rechazado';
      default:
        return estado;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
        Verificaciones Pendientes
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        {verificaciones.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" color="textSecondary">
              No hay verificaciones pendientes
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Todas las verificaciones han sido procesadas
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Fecha</TableCell>
                  <TableCell>Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {verificaciones.map((verificacion) => (
                  <TableRow key={verificacion.id}>
                    <TableCell>{verificacion.id}</TableCell>
                    <TableCell>
                      <Chip
                        label={getEstadoText(verificacion.estado)}
                        color={getEstadoColor(verificacion.estado) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(verificacion.fechaCreacion).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<Visibility />}
                          onClick={() => handleVerDetalles(verificacion)}
                        >
                          Ver
                        </Button>
                        {verificacion.estado === 'PENDIENTE' && (
                          <>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<CheckCircle />}
                              onClick={() => handleAprobar(verificacion)}
                            >
                              Aprobar
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              startIcon={<Cancel />}
                              onClick={() => handleRechazar(verificacion)}
                            >
                              Rechazar
                            </Button>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog para ver detalles */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalles de Verificación</DialogTitle>
        <DialogContent>
          {selectedVerificacion && (
            <>
              {console.log('🎨 Renderizando modal con selectedVerificacion:', selectedVerificacion)}
              {console.log('👤 Cliente en modal:', selectedVerificacion.cliente)}
              {console.log('📁 Documentos en modal:', selectedVerificacion.documentos)}
              {console.log('🔍 Verificando datos del cliente:', {
                nombre: selectedVerificacion.cliente?.nombre,
                email: selectedVerificacion.cliente?.email,
                telefono: selectedVerificacion.cliente?.telefono
              })}
              {console.log('🔍 Verificando documentos:', {
                dni: selectedVerificacion.documentos?.dni,
                selfie: selectedVerificacion.documentos?.selfie,
                personeria: selectedVerificacion.documentos?.personeria
              })}
              <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Información del Cliente
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Person sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2">
                            <strong>Nombre:</strong> {selectedVerificacion.cliente?.nombre || selectedVerificacion.datos?.nombre || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Email sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2">
                            <strong>Email:</strong> {selectedVerificacion.cliente?.email || selectedVerificacion.datos?.email || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Phone sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2">
                            <strong>Teléfono:</strong> {selectedVerificacion.cliente?.telefono || selectedVerificacion.datos?.telefono || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2">
                            <strong>Fecha:</strong> {new Date(selectedVerificacion.fechaCreacion).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Description sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2">
                            <strong>Documento:</strong> {selectedVerificacion.cliente?.tipoDocumento} {selectedVerificacion.cliente?.numeroDocumento}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Business sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2">
                            <strong>Tipo:</strong> {selectedVerificacion.cliente?.esEmpresa ? 'Empresa' : 'Persona'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Documentos
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>DNI:</strong> {selectedVerificacion.documentos?.dni ? '✅ Subido' : '❌ No subido'}
                          </Typography>
                          {selectedVerificacion.documentos?.dni && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => window.open(selectedVerificacion.documentos.dni, '_blank')}
                            >
                              Ver DNI
                            </Button>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Selfie:</strong> {selectedVerificacion.documentos?.selfie ? '✅ Subido' : '❌ No subido'}
                          </Typography>
                          {selectedVerificacion.documentos?.selfie && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => window.open(selectedVerificacion.documentos.selfie, '_blank')}
                            >
                              Ver Selfie
                            </Button>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Personería:</strong> {selectedVerificacion.documentos?.personeria ? '✅ Subido' : '❌ No subido'}
                          </Typography>
                          {selectedVerificacion.documentos?.personeria && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => window.open(selectedVerificacion.documentos.personeria, '_blank')}
                            >
                              Ver Personería
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para confirmar acción */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)}>
        <DialogTitle>
          {action === 'APPROVE' ? 'Aprobar Verificación' : 'Rechazar Verificación'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ¿Estás seguro de que quieres {action === 'APPROVE' ? 'aprobar' : 'rechazar'} la verificación ID: {selectedVerificacion?.id}?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Comentarios (opcional)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder={action === 'APPROVE' ? 'Comentarios sobre la aprobación...' : 'Motivo del rechazo...'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)} disabled={processing}>
            Cancelar
          </Button>
          <Button
            onClick={confirmarAccion}
            variant="contained"
            color={action === 'APPROVE' ? 'success' : 'error'}
            disabled={processing}
            startIcon={processing ? <CircularProgress size={20} /> : undefined}
          >
            {processing ? 'Procesando...' : (action === 'APPROVE' ? 'Aprobar' : 'Rechazar')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default VerificacionesPendientes;
